import { h } from '../lib/dom.js';
import { apiDelete, apiPost } from '../lib/api.js';
import { confirmModal, toast } from '../lib/ui.js';
import { fmtDateRange, fmtDuration } from '../lib/format.js';

const MONTHS_SHORT = ['Oca','Şub','Mar','Nis','May','Haz','Tem','Ağu','Eyl','Eki','Kas','Ara'];
const monthShort = iso => MONTHS_SHORT[new Date(iso).getMonth()];
const dayNum = iso => String(new Date(iso).getDate());

const BADGE_LABELS = {
  current: 'Bu hafta',
  upcoming: 'Yaklaşan',
  past: 'Geçmiş',
  completed: 'Tamamlandı',
  archived: 'Arşiv',
};

/**
 * Zengin hafta kartı — koç ve öğrenci panellerinde ortak.
 *
 * @param w              — /api/weeks response satırı (task_count, completed_count, actual_minutes içermeli)
 * @param opts.kind      — 'current' | 'upcoming' | 'past' | 'completed' | 'archived'
 * @param opts.refresh   — callback: mutation sonrası listeyi yeniden çiz
 * @param opts.canManage — true → Düzenle/Arşivle butonları (koç); false → yalnız görüntüle (öğrenci)
 * @param opts.hrefBase  — kartın açılacağı yol prefix'i (ör. '/coach/weeks' veya '/student/weeks')
 * @param opts.onEdit    — canManage=true iken tetiklenecek düzenleme handler'ı
 */
export function weekCard(w, opts = {}) {
  const {
    kind = 'past',
    refresh = () => {},
    canManage = false,
    hrefBase = '/coach/weeks',
    onEdit,
  } = opts;
  const isArchived = kind === 'archived';
  const isCurrent = kind === 'current';
  const badgeLabel = BADGE_LABELS[kind] || '';
  const completionPct = w.task_count > 0 ? Math.round((w.completed_count / w.task_count) * 100) : 0;
  const targetMin = Math.round((Number(w.target_hours) || 0) * 60);

  const medallion = h('div', { class: 'week-medallion' },
    h('span', { class: 'm-mon' }, monthShort(w.start_date)),
    h('span', { class: 'm-range' }, dayNum(w.start_date) + '–' + dayNum(w.end_date)),
    h('span', { class: 'm-sub' }, String(new Date(w.start_date).getFullYear())),
  );

  const stats = h('div', { class: 'week-card-stats' },
    h('span', { class: 'stat-inline' },
      h('strong', {}, (w.completed_count ?? 0) + ' / ' + (w.task_count ?? 0)),
      'görev',
    ),
    h('span', { class: 'stat-inline' },
      h('strong', {}, fmtDuration(w.actual_minutes ?? 0)),
      targetMin > 0 ? ('/ ' + fmtDuration(targetMin) + ' hedef') : '',
    ),
    w.task_count > 0
      ? h('span', { class: 'stat-inline' }, h('strong', {}, completionPct + '%'), 'tamamlama')
      : null,
  );

  const progress = w.task_count > 0
    ? h('div', { class: 'week-card-progress progress' },
        h('div', {
          class: 'progress-bar' + (completionPct >= 100 ? ' ok' : (completionPct < 40 ? ' warn' : '')),
          style: { width: completionPct + '%' },
        }))
    : null;

  const actions = h('div', { class: 'week-card-actions' });
  if (isArchived && canManage) {
    actions.appendChild(h('button', {
      class: 'btn btn-primary btn-sm',
      onclick: async (e) => {
        e.preventDefault(); e.stopPropagation();
        try {
          await apiPost('/weeks/' + w.id + '/restore', {});
          toast('Hafta geri aktifleştirildi', { type: 'success' });
          refresh();
        } catch (err) { toast(err.message, { type: 'error' }); }
      },
    }, 'Aktif et'));
    actions.appendChild(h('a', {
      class: 'btn btn-ghost btn-sm',
      href: '#' + hrefBase + '/' + w.id,
    }, 'Görüntüle'));
  } else if (canManage) {
    actions.appendChild(h('a', { class: 'btn btn-primary btn-sm', href: '#' + hrefBase + '/' + w.id }, 'Plan yap'));
    if (typeof onEdit === 'function') {
      actions.appendChild(h('button', {
        class: 'btn btn-ghost btn-sm',
        onclick: (e) => { e.preventDefault(); e.stopPropagation(); onEdit(w); },
      }, 'Düzenle'));
    }
    actions.appendChild(h('button', {
      class: 'btn btn-danger btn-sm',
      onclick: (e) => {
        e.preventDefault(); e.stopPropagation();
        confirmModal({
          title: 'Haftayı arşivle',
          message: (w.title || fmtDateRange(w.start_date, w.end_date))
            + ' arşivlenecek. Görevler ve geçmiş veriler korunur.',
          confirmText: 'Arşivle', danger: true,
          onConfirm: async () => {
            await apiDelete('/weeks/' + w.id);
            toast('Hafta arşivlendi');
            refresh();
          },
        });
      },
    }, 'Arşivle'));
  } else {
    // Öğrenci: sadece aç
    actions.appendChild(h('a', {
      class: 'btn btn-primary btn-sm',
      href: '#' + hrefBase + '/' + w.id,
    }, 'Haftayı aç'));
  }

  return h('div', {
    class: 'week-card' + (isCurrent ? ' current' : '') + (isArchived ? ' archived' : ''),
  },
    medallion,
    h('div', { class: 'week-card-body' },
      badgeLabel ? h('span', { class: 'week-badge b-' + kind }, badgeLabel) : null,
      h('div', { class: 'week-card-title' }, w.title || fmtDateRange(w.start_date, w.end_date)),
      h('div', { class: 'muted', style: { fontSize: 'var(--fs-small)' } },
        fmtDateRange(w.start_date, w.end_date)),
      w.weekly_goal_text ? h('div', { class: 'week-card-goal' }, w.weekly_goal_text) : null,
      stats,
      progress,
    ),
    actions,
  );
}

/** Grup başlığı + kartlar bir section içinde. */
export function weekGroup(label, items, kind, buildCard) {
  if (!items.length) return null;
  const list = h('div', { class: 'stack' });
  items.forEach(w => list.appendChild(buildCard(w, kind)));
  return h('section', {},
    h('div', { class: 'section-header' },
      h('h3', {}, label),
      h('span', { class: 'count' }, items.length + ' hafta'),
    ),
    list,
  );
}

/** 3'lü sekme (view switch) — Aktif / Tamamlanan / (opsiyonel) Arşiv. */
export function viewSwitch(basePath, currentView, tabs) {
  return h('div', { class: 'view-switch' },
    ...tabs.map(([v, label]) => h('a', {
      href: v === 'active' ? '#' + basePath : '#' + basePath + '?view=' + v,
      class: 'btn btn-sm ' + (currentView === v ? 'btn-primary' : 'btn-ghost'),
      'aria-current': currentView === v ? 'page' : null,
    }, label)),
  );
}

/** Hafta tamamlandı mı? — tüm görevler completed AND en az 1 görev var. */
export function isWeekFinished(w) {
  return Number(w.task_count) > 0 && Number(w.completed_count) === Number(w.task_count);
}

/** Aktif/upcoming/past/completed sınıflandırması + doğru sıralama. */
export function groupWeeks(weeks, todayIso) {
  const active = weeks.filter(w => !w.deleted_at && !isWeekFinished(w));
  const finished = weeks.filter(w => !w.deleted_at && isWeekFinished(w));
  const archived = weeks.filter(w => w.deleted_at);
  return {
    current:  active.filter(w => todayIso >= w.start_date && todayIso <= w.end_date),
    upcoming: active.filter(w => todayIso <  w.start_date).sort((a,b) => a.start_date.localeCompare(b.start_date)),
    past:     active.filter(w => todayIso >  w.end_date  ).sort((a,b) => b.start_date.localeCompare(a.start_date)),
    completed: finished.sort((a,b) => b.end_date.localeCompare(a.end_date)),
    archived:  archived.sort((a,b) => b.start_date.localeCompare(a.start_date)),
  };
}
